// src/components/AvaliacaoDesempenho.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db, auth } from '../../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, orderBy, writeBatch } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaPlus, FaCheckCircle, FaLock, FaFileAlt, FaProjectDiagram, FaCalendarAlt, FaSearch, FaEdit, FaCopy, FaTrash } from 'react-icons/fa';
import '../../styles/AvaliacaoDesempenho.css';

// --- COMPONENTE: MODAL DE CONFIRMAÇÃO DE EXCLUSÃO ---
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, isDeleting }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h2>{title}</h2>
                <p>{message}</p>
                <div className="modal-actions">
                    <button onClick={onClose} className="btn btn-secondary" disabled={isDeleting}>
                        Cancelar
                    </button>
                    <button onClick={onConfirm} className="btn btn-danger" disabled={isDeleting}>
                        {isDeleting ? 'Excluindo...' : 'Confirmar Exclusão'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE: NOTIFICAÇÃO VISÍVEL ---
const Notification = ({ message, type, onClose }) => {
    if (!message) return null;
    return (
        <div className={`notification-fixed notification-${type}`}>
            <span>{message}</span>
            <button onClick={onClose} className="close-btn">&times;</button>
        </div>
    );
};


const AvaliacaoDesempenho = ({ userData }) => {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminEmpresaId, setAdminEmpresaId] = useState(userData?.empresa_id || null);
  const navigate = useNavigate();
  const location = useLocation(); 

  // --- Estados para a funcionalidade de exclusão ---
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cycleToDelete, setCycleToDelete] = useState(null);

  // --- Estados para filtros e notificações ---
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const statusMap = {
    active: { text: 'Ativo', icon: <FaCheckCircle /> },
    closed: { text: 'Fechado', icon: <FaLock /> },
    draft: { text: 'Rascunho', icon: <FaFileAlt /> },
  };
  
  // useEffect para exibir a notificação recebida via navegação
  useEffect(() => {
    if (location.state?.message) {
      setNotification({ message: location.state.message, type: location.state.type || 'success' });
      // Limpa o estado da rota para não reexibir a mensagem ao recarregar a página
      window.history.replaceState({}, document.title);
      
      const timer = setTimeout(() => { setNotification({ message: '', type: '' }); }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // useEffect para buscar dados do administrador se não forem passados via props
  useEffect(() => {
    if (!adminEmpresaId) {
        const fetchAdminData = async () => {
          const user = auth.currentUser;
          if (user) {
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists() && userDocSnap.data().empresa_id) {
              setAdminEmpresaId(userDocSnap.data().empresa_id);
            } else {
              setError("ID da empresa não encontrado para o usuário.");
              setLoading(false);
            }
          } else { navigate('/login'); }
        };
        fetchAdminData();
    }
  }, [adminEmpresaId, navigate]);

  // Função para buscar todos os ciclos de avaliação da empresa
  const fetchCycles = useCallback(async () => {
    if (!adminEmpresaId) return;
    setLoading(true);
    try {
      const cyclesRef = collection(db, 'Empresas', adminEmpresaId, 'evaluationCycles');
      const q = query(cyclesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const cyclesList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const cyclesWithProgress = await Promise.all(
        cyclesList.map(async (cycle) => {
          const responsesRef = collection(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycle.id, 'responses');
          const responsesSnap = await getDocs(responsesRef);
          const total = responsesSnap.size; // `total` é o nosso `totalCount`

          if (total === 0) {
            return { ...cycle, progress: 0, completedCount: 0, totalCount: 0 };
          }
          
          const completedQuery = query(responsesRef, where('status', '==', 'submitted'));
          const completedSnap = await getDocs(completedQuery);
          const completed = completedSnap.size;
          const progress = Math.round((completed / total) * 100);

          return { ...cycle, progress, completedCount: completed, totalCount: total };
        })
      );

      setCycles(cyclesWithProgress);
    } catch (err) {
      console.error("Erro ao buscar ciclos de avaliação:", err);
      setError('Falha ao carregar os ciclos de avaliação.');
    } finally {
      setLoading(false);
    }
  }, [adminEmpresaId]);

  useEffect(() => {
    if (adminEmpresaId) { fetchCycles(); }
  }, [adminEmpresaId, fetchCycles]);
  
  const filteredCycles = useMemo(() => {
    return cycles.filter(cycle => {
      const searchMatch = cycle.name.toLowerCase().includes(searchTerm.toLowerCase());
      const statusMatch = statusFilter === 'all' || cycle.status === statusFilter;
      return searchMatch && statusMatch;
    });
  }, [cycles, searchTerm, statusFilter]);

  const getStatusClass = (status) => `status-${status}`;

  // --- Funções para controlar o modal e a exclusão ---
  const openDeleteModal = (cycle) => {
    setCycleToDelete(cycle);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setCycleToDelete(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    if (!cycleToDelete) return;
    setIsDeleting(true);

    try {
        const cycleRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycleToDelete.id);
        const responsesRef = collection(cycleRef, 'responses');
        const participantsRef = collection(cycleRef, 'participants');
        
        const batch = writeBatch(db);

        const responsesSnap = await getDocs(responsesRef);
        responsesSnap.forEach(doc => batch.delete(doc.ref));

        const participantsSnap = await getDocs(participantsRef);
        participantsSnap.forEach(doc => batch.delete(doc.ref));

        batch.delete(cycleRef);

        await batch.commit();

        setCycles(prevCycles => prevCycles.filter(c => c.id !== cycleToDelete.id));
        setNotification({ message: 'Ciclo excluído com sucesso!', type: 'success' });

    } catch (error) {
        console.error("Erro ao excluir o ciclo:", error);
        setNotification({ message: `Falha ao excluir o ciclo: ${error.message}`, type: 'error' });
    } finally {
        setIsDeleting(false);
        closeDeleteModal();
    }
  };

  if (loading) return <div className="avaliacao-container"><p>Carregando avaliações...</p></div>;
  if (error) return <div className="avaliacao-container"><p className="error-message">{error}</p></div>;

  return (
    <div className="avaliacao-container">
      <Notification 
        message={notification.message} 
        type={notification.type} 
        onClose={() => setNotification({ message: '', type: '' })} 
      />
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message={`Você tem certeza que deseja excluir permanentemente o ciclo "${cycleToDelete?.name}"? Esta ação não pode ser desfeita.`}
        isDeleting={isDeleting}
      />

      <div className="avaliacao-header">
        <h1>Ciclos de Avaliação</h1>
        {userData?.departamento === 'People' && (
            <button onClick={() => navigate('/avaliacoes/criar')} className="btn btn-primary">
                <FaPlus /> Criar Novo Ciclo
            </button>
        )}
      </div>

      <div className="cycle-filters">
        <input 
          type="text"
          className="search-input"
          placeholder="Pesquisar por nome do ciclo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="status-filter-buttons">
          <button onClick={() => setStatusFilter('all')} className={`btn-filter ${statusFilter === 'all' ? 'active' : ''}`}>Todos</button>
          <button onClick={() => setStatusFilter('active')} className={`btn-filter ${statusFilter === 'active' ? 'active' : ''}`}>Ativos</button>
          <button onClick={() => setStatusFilter('closed')} className={`btn-filter ${statusFilter === 'closed' ? 'active' : ''}`}>Fechados</button>
          <button onClick={() => setStatusFilter('draft')} className={`btn-filter ${statusFilter === 'draft' ? 'active' : ''}`}>Rascunhos</button>
        </div>
      </div>

      {filteredCycles.length > 0 ? (
        <div className="cycles-list">
          {filteredCycles.map(cycle => {
            const statusInfo = statusMap[cycle.status] || { text: cycle.status || 'Não definido', icon: null };
            const canEdit = cycle.status !== 'closed';
            const canDelete = userData?.departamento === 'People' && cycle.totalCount === 0;

            return (
              <div key={cycle.id} className="cycle-card">
                <div className="cycle-card-header" onClick={() => navigate(`/avaliacoes/progresso/${cycle.id}`)}>
                  <h3>{cycle.name}</h3>
                  <span className={`cycle-status ${getStatusClass(cycle.status)}`}>
                    {statusInfo.icon} {statusInfo.text}
                  </span>
                </div>
                <div className="card-content" onClick={() => navigate(`/avaliacoes/progresso/${cycle.id}`)}>
                  <div className="card-info-row">
                    <i><FaProjectDiagram /></i>
                    <p><strong>Metodologia:</strong> {cycle.methodology}</p>
                  </div>
                  <div className="card-info-row">
                    <i><FaCalendarAlt /></i>
                    <p><strong>Período:</strong> {cycle.startDate?.toDate().toLocaleDateString('pt-BR')} a {cycle.endDate?.toDate().toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div className="progress-info">
                    <div className="progress-text">
                      <strong>Progresso</strong>
                      <span>{cycle.completedCount} de {cycle.totalCount}</span>
                    </div>
                    <div className="progress-bar-container">
                      <div className="progress-bar-fill" style={{ width: `${cycle.progress}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className="cycle-card-footer">
                  <div className="card-actions">
                    {canEdit && userData?.departamento === 'People' && (
                        <button className="action-btn" title="Editar" onClick={(e) => { e.stopPropagation(); navigate(`/avaliacoes/editar/${cycle.id}`); }}>
                            <FaEdit />
                        </button>
                    )}
                    {userData?.departamento === 'People' && (
                        <button className="action-btn" title="Duplicar" onClick={(e) => { e.stopPropagation(); navigate(`/avaliacoes/duplicar/${cycle.id}`); }}>
                            <FaCopy />
                        </button>
                    )}
                    {canDelete && (
                        <button 
                          className="action-btn delete-btn" 
                          title="Excluir Ciclo" 
                          onClick={(e) => { e.stopPropagation(); openDeleteModal(cycle); }}>
                            <FaTrash />
                        </button>
                    )}
                  </div>
                  <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); navigate(`/avaliacoes/progresso/${cycle.id}`); }}>
                    Ver Progresso
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="empty-state-container">
            <div className="empty-state-icon"><FaSearch /></div>
            <h3>Nenhum Ciclo Encontrado</h3>
            <p>Nenhum ciclo corresponde aos seus critérios de busca. Tente um filtro diferente ou crie um novo ciclo.</p>
        </div>
      )}
    </div>
  );
};

export default AvaliacaoDesempenho;